import React, { useState } from "react";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Pencil, Plus, Save, Trash2, X, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const EMPTY = { location: "", address: "", date: "", time: "", flavorsCsv: "" };

const toCsv = (arr) => (arr || []).join(", ");
const fromCsv = (s) =>
  (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const formatDate = (iso) => {
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

export const PopUpsManager = ({ popups, onChange }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errs, setErrs] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setErrs({});
    setShowForm(true);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      location: p.location,
      address: p.address,
      date: p.date,
      time: p.time,
      flavorsCsv: toCsv(p.flavors),
    });
    setErrs({});
    setShowForm(true);
  };

  const cancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY);
    setErrs({});
  };

  const validate = () => {
    const e = {};
    if (!form.location.trim()) e.location = "Required";
    if (!form.address.trim()) e.address = "Required";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) e.date = "YYYY-MM-DD";
    if (!form.time.trim()) e.time = "Required";
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const save = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const payload = {
      location: form.location.trim(),
      address: form.address.trim(),
      date: form.date,
      time: form.time.trim(),
      flavors: fromCsv(form.flavorsCsv),
    };
    try {
      if (editingId) {
        const r = await api.put(`/popups/${editingId}`, payload);
        toast.success("Pop-up updated");
        onChange((list) => list.map((p) => (p.id === editingId ? r.data : p)));
      } else {
        const r = await api.post("/popups", payload);
        toast.success("Pop-up added");
        onChange((list) => [...list, r.data]);
      }
      cancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save pop-up");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete pop-up "${p.location}"?`)) return;
    setDeletingId(p.id);
    try {
      await api.delete(`/popups/${p.id}`);
      onChange((list) => list.filter((x) => x.id !== p.id));
      toast.success("Pop-up deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setDeletingId(null);
    }
  };

  const sorted = [...popups].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div data-testid="popups-manager">
      <div className="flex items-center justify-between mb-6">
        <div data-testid="popups-manager-count" className="text-sm text-[#8D99AE]">
          {popups.length} {popups.length === 1 ? "pop-up" : "pop-ups"}
        </div>
        {!showForm && (
          <button
            data-testid="popups-add-new"
            onClick={startCreate}
            className="inline-flex items-center gap-2 bg-[#2C1E16] text-[#FAF9F6] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#3E2D24] transition-colors"
          >
            <Plus size={15} strokeWidth={1.6} />
            Add pop-up
          </button>
        )}
      </div>

      {showForm && (
        <form
          data-testid="popup-form"
          onSubmit={save}
          className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl text-[#2C1E16]">
              {editingId ? "Edit pop-up" : "New pop-up"}
            </h3>
            <button type="button" onClick={cancel} className="text-[#8D99AE] hover:text-[#2C1E16]" data-testid="popup-form-cancel">
              <X size={18} strokeWidth={1.6} />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Location" testId="popup-input-location" error={errs.location}>
              <input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Overton Park"
                className="admin-input"
              />
            </Field>
            <Field label="Address" testId="popup-input-address" error={errs.address}>
              <input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="1914 Poplar Ave, Memphis, TN"
                className="admin-input"
              />
            </Field>
            <Field label="Date" testId="popup-input-date" error={errs.date}>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="admin-input"
              />
            </Field>
            <Field label="Time" testId="popup-input-time" error={errs.time}>
              <input
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                placeholder="9:00 AM – 1:00 PM"
                className="admin-input"
              />
            </Field>
            <Field label="Flavors available (comma-separated)" testId="popup-input-flavors" className="sm:col-span-2">
              <input
                value={form.flavorsCsv}
                onChange={(e) => setForm({ ...form, flavorsCsv: e.target.value })}
                placeholder="Vanilla Latte, Spanish Latte"
                className="admin-input"
              />
            </Field>
          </div>

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
            <button
              type="button"
              onClick={cancel}
              data-testid="popup-form-cancel-bottom"
              className="text-sm px-5 py-2.5 text-[#5C4E46] hover:text-[#2C1E16]"
            >
              Cancel
            </button>
            <button
              data-testid="popup-form-save"
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] disabled:opacity-60 transition-colors rounded-full px-6 py-2.5 text-sm font-medium"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} strokeWidth={1.6} />}
              {editingId ? "Save changes" : "Create pop-up"}
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-3">
        {sorted.length === 0 && (
          <div data-testid="popups-manager-empty" className="rounded-3xl border border-[#E8E3DD] bg-white p-10 text-center text-[#5C4E46]">
            No pop-ups yet. Add your first one above.
          </div>
        )}
        {sorted.map((p) => (
          <article
            key={p.id}
            data-testid={`popup-row-${p.id}`}
            className="rounded-2xl border border-[#E8E3DD] bg-white p-5 grid sm:grid-cols-12 gap-3 items-start"
          >
            <div className="sm:col-span-4">
              <div className="font-display text-lg leading-tight text-[#2C1E16] flex items-start gap-2">
                <MapPin size={16} strokeWidth={1.5} className="mt-1 shrink-0" />
                {p.location}
              </div>
              <div className="text-xs text-[#8D99AE] mt-0.5 pl-6">{p.address}</div>
            </div>
            <div className="sm:col-span-4 text-sm text-[#5C4E46] flex flex-col gap-1">
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={14} strokeWidth={1.5} />
                {formatDate(p.date)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} strokeWidth={1.5} />
                {p.time}
              </span>
            </div>
            <div className="sm:col-span-3 flex flex-wrap gap-1.5">
              {p.flavors?.length ? (
                p.flavors.map((f) => (
                  <span key={f} className="text-[11px] text-[#2C1E16] bg-[#D4A373]/15 px-2.5 py-0.5 rounded-full">
                    {f}
                  </span>
                ))
              ) : (
                <span className="text-xs text-[#8D99AE] italic">No flavors set</span>
              )}
            </div>
            <div className="sm:col-span-1 flex sm:justify-end gap-2">
              <button
                data-testid={`popup-edit-${p.id}`}
                onClick={() => startEdit(p)}
                className="p-2 rounded-full hover:bg-[#FAF9F6] text-[#2C1E16]"
                title="Edit"
              >
                <Pencil size={15} strokeWidth={1.6} />
              </button>
              <button
                data-testid={`popup-delete-${p.id}`}
                onClick={() => remove(p)}
                disabled={deletingId === p.id}
                className="p-2 rounded-full hover:bg-[#FAF9F6] text-[#B33A3A] disabled:opacity-50"
                title="Delete"
              >
                {deletingId === p.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} strokeWidth={1.6} />}
              </button>
            </div>
          </article>
        ))}
      </div>

      <style>{`
        .admin-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid #E8E3DD;
          color: #2C1E16;
          padding: 0.6rem 0;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.2s;
        }
        .admin-input:focus { border-bottom-color: #2C1E16; }
        .admin-input::placeholder { color: #B6AEA5; }
      `}</style>
    </div>
  );
};

const Field = ({ label, children, testId, error, className = "" }) => (
  <label className={`block ${className}`} data-testid={testId}>
    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-1">{label}</span>
    {children}
    {error && <span className="mt-1 block text-xs text-[#B33A3A]">{error}</span>}
  </label>
);
